import {
  ConnectionQuality,
  RemoteTrack,
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type Participant,
  type RemoteParticipant,
  type RemoteTrackPublication,
} from 'livekit-client';
import { useStore } from '../store';
import { api } from './api';
import { gateway } from './gateway';
import { getSettings, screenShareUnavailableReason, setSettings } from './platform';

const QUALITY_MAP: Record<string, 'excellent' | 'good' | 'poor' | 'unknown'> = {
  [ConnectionQuality.Excellent]: 'excellent',
  [ConnectionQuality.Good]: 'good',
  [ConnectionQuality.Poor]: 'poor',
  [ConnectionQuality.Unknown]: 'unknown',
};

/**
 * Toda a midia passa pelo LiveKit (SFU). Este modulo e a unica parte do app
 * que fala com ele: entra na sala do canal, publica microfone/camera/tela e
 * espelha o que esta acontecendo dentro do store para a UI desenhar.
 */
/** Volume salvo para uma pessoa. Sem ajuste, e 1 (normal). */
function volumeDe(identity: string): number {
  return useStore.getState().volumes[identity] ?? 1;
}

export type QualidadeTela = 'leve' | 'equilibrada' | 'nitida';

/**
 * Tres opcoes em vez de um numero livre: a pessoa sabe se esta travando, nao
 * quantos kbps cabem no upload dela.
 *
 * "leve" existe para quem esta na 4G ou com upload ruim — 720p a 15 quadros
 * gasta um terco do que 1080p a 30 gasta, e para tela parada (codigo, slide)
 * a diferenca quase nao aparece.
 */
const QUALIDADES = {
  leve: {
    resolution: { width: 1280, height: 720, frameRate: 15 },
    encoding: { maxBitrate: 800_000, maxFramerate: 15 },
  },
  equilibrada: {
    resolution: { width: 1280, height: 720, frameRate: 30 },
    encoding: { maxBitrate: 1_800_000, maxFramerate: 30 },
  },
  nitida: {
    resolution: { width: 1920, height: 1080, frameRate: 30 },
    encoding: { maxBitrate: 3_000_000, maxFramerate: 30 },
  },
} as const;

export function qualidadeDaTelaEscolhida(): QualidadeTela {
  const salva = getSettings().qualidadeTela;
  return salva === 'leve' || salva === 'nitida' ? salva : 'equilibrada';
}

export function definirQualidadeDaTela(valor: QualidadeTela) {
  setSettings({ ...getSettings(), qualidadeTela: valor });
}

function qualidadeDaTela() {
  return QUALIDADES[qualidadeDaTelaEscolhida()];
}

class VoiceManager {
  private room: Room | null = null;
  /** Elementos <audio> criados para cada faixa remota, para controlar volume. */
  private audioElements = new Map<string, HTMLAudioElement>();
  /**
   * Canal cuja conexao esta em andamento AGORA.
   *
   * Guardado aqui, e nao no store, porque entre pedir para entrar e o estado
   * virar "connecting" existe um `await`: nesse intervalo o React pode
   * renderizar de novo, um segundo `entrar` no mesmo canal passaria pela
   * checagem e o `leave` dele derrubaria a sala que o primeiro estava abrindo —
   * que aparece como "Client initiated disconnect".
   */
  private conectandoA: string | null = null;

  get currentRoom() {
    return this.room;
  }

  async join(guildId: string, channelId: string) {
    await this.conectar(guildId, channelId, () => api.voiceToken(channelId));
  }

  /**
   * Ligacao privada: mesma conexao de sempre, so muda de onde vem o token. A
   * sala e exclusiva do par, e `guildId` fica nulo porque a conversa nao
   * pertence a guild nenhuma.
   */
  async ligarNoPrivado(dmChannelId: string) {
    await this.conectar(null, dmChannelId, async () => {
      const grant = await api.ligacaoPrivada(dmChannelId);
      return {
        ...grant,
        canPublish: grant.canPublish as Array<
          'microphone' | 'camera' | 'screen_share' | 'screen_share_audio'
        >,
      };
    });
  }

  private async conectar(
    guildId: string | null,
    channelId: string,
    obterGrant: () => Promise<{
      token: string;
      url: string;
      room: string;
      canPublish: Array<'microphone' | 'camera' | 'screen_share' | 'screen_share_audio'>;
    }>,
  ) {
    const store = useStore.getState();
    if (this.room && store.voice.channelId === channelId) return;
    // Ja ha uma conexao em andamento para este mesmo canal: a segunda chamada
    // so serviria para derrubar a primeira.
    if (this.conectandoA === channelId) return;

    this.conectandoA = channelId;
    if (this.room) await this.leave(false);

    store.patchVoice({ guildId, channelId, status: 'connecting', error: null });

    try {
      const grant = await obterGrant();

      const room = new Room({
        adaptiveStream: true,
        // Dynacast desliga camadas de video que ninguem esta assistindo.
        dynacast: true,
        /*
         * O navegador liga cancelamento de eco e reducao de ruido por padrao,
         * mas o padrao muda entre navegadores e versoes. Declarar aqui garante
         * o mesmo tratamento em todo mundo — sem eco, que e o defeito que mais
         * estraga uma call de 12 pessoas.
         */
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        publishDefaults: {
          simulcast: true,
          // Tela em 1080p costuma ser texto/codigo: prioriza nitidez.
          screenShareEncoding: { maxBitrate: 3_000_000, maxFramerate: 30 },
          videoCodec: 'vp9',
          /*
           * RED repete o audio em pacotes seguintes: numa rede que perde
           * pacotes, a voz continua inteligivel em vez de picotar. Custa uns
           * poucos kbps, o que e barato perto de repetir "nao te ouvi".
           */
          red: true,
          /*
           * DTX para de transmitir no silencio. Com 12 pessoas, quase todas
           * caladas quase o tempo todo, e onde mais se economiza banda — e o
           * gargalo aqui e o encaminhamento no unico nucleo da VPS.
           */
          dtx: true,
        },
      });
      this.room = room;
      this.wire(room);

      await room.connect(grant.url, grant.token);

      const canSpeak = grant.canPublish.includes('microphone');
      if (canSpeak) {
        await room.localParticipant.setMicrophoneEnabled(!store.voice.deafened);
      }

      useStore.getState().patchVoice({
        status: 'connected',
        canPublish: grant.canPublish,
        micEnabled: canSpeak && !store.voice.deafened,
        sharingScreen: false,
        cameraEnabled: false,
      });

      /*
       * O estado de voz e por guild: quem esta em qual canal aparece na barra
       * lateral daquele servidor. Ligacao privada nao tem guild e nao deve
       * aparecer para ninguem, entao nao anuncia nada.
       */
      if (guildId) {
        gateway.updateVoiceState(guildId, channelId, {
          selfMute: !canSpeak || store.voice.deafened,
          selfDeaf: store.voice.deafened,
        });
      }

      this.syncParticipants();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao entrar no canal de voz.';
      useStore.getState().patchVoice({ status: 'idle', channelId: null, error: message });
      useStore.getState().toast(message, 'error');
      await this.leave(true);
    } finally {
      // Sempre libera, inclusive no erro: senao um canal que falhou uma vez
      // ficaria travado para sempre nesta aba.
      if (this.conectandoA === channelId) this.conectandoA = null;
    }
  }

  private wire(room: Room) {
    const store = () => useStore.getState();

    room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        const element = track.attach() as HTMLAudioElement;
        element.dataset.identity = participant.identity;
        // Quem entrou depois de voce ter ajustado o volume dela precisa nascer
        // ja no volume escolhido, nao no padrao.
        element.volume = store().voice.deafened ? 0 : volumeDe(participant.identity);
        document.body.appendChild(element);
        this.audioElements.set(publication.trackSid, element);
        return;
      }
      const kind = publication.source === Track.Source.ScreenShare ? 'screen' : 'camera';
      store().setTrack(participant.identity, kind, track);
    });

    room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        const element = this.audioElements.get(publication.trackSid);
        element?.remove();
        this.audioElements.delete(publication.trackSid);
        track.detach();
        return;
      }
      const kind = publication.source === Track.Source.ScreenShare ? 'screen' : 'camera';
      store().setTrack(participant.identity, kind, undefined);
      if (store().voice.focusedIdentity === participant.identity && kind === 'screen') {
        store().patchVoice({ focusedIdentity: null });
      }
    });

    /*
     * Quando alguem silencia a camera sem despublicar, a faixa continua ali mas
     * para de entregar imagem: o tile mostraria um retangulo preto. Tratamos
     * silenciar como "sem video" e restauramos ao voltar.
     */
    room.on(RoomEvent.TrackMuted, (publication, participant) => {
      if (publication.kind !== Track.Kind.Video) return;
      const kind = publication.source === Track.Source.ScreenShare ? 'screen' : 'camera';
      store().setTrack(participant.identity, kind, undefined);
    });

    room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
      if (publication.kind !== Track.Kind.Video) return;
      const track = publication.track;
      if (!track) return;
      const kind = publication.source === Track.Source.ScreenShare ? 'screen' : 'camera';
      store().setTrack(participant.identity, kind, track as Track);
    });

    room.on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication) => {
      const track = publication.track;
      if (!track || track.kind === Track.Kind.Audio) return;
      const kind = publication.source === Track.Source.ScreenShare ? 'screen' : 'camera';
      store().setTrack(room.localParticipant.identity, kind, track);
    });

    room.on(RoomEvent.LocalTrackUnpublished, (publication: LocalTrackPublication) => {
      const kind = publication.source === Track.Source.ScreenShare ? 'screen' : 'camera';
      store().setTrack(room.localParticipant.identity, kind, undefined);
      // O navegador tem a propria barra "parar de compartilhar": o estado
      // precisa acompanhar mesmo quando a parada nao vem do nosso botao.
      if (kind === 'screen' && store().voice.sharingScreen) {
        store().patchVoice({ sharingScreen: false });
        const { guildId, channelId } = store().voice;
        if (guildId && channelId) {
          gateway.updateVoiceState(guildId, channelId, { streaming: false });
        }
      }
    });

    room.on(RoomEvent.ParticipantConnected, () => this.syncParticipants());
    room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
      store().removeVoiceParticipant(participant.identity);
    });

    room.on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
      const speaking = new Set(speakers.map((s) => s.identity));
      const current = store().voiceParticipants;
      for (const [identity, participant] of Object.entries(current)) {
        const isSpeaking = speaking.has(identity);
        if (participant.speaking !== isSpeaking) {
          store().setVoiceParticipant({ ...participant, speaking: isSpeaking });
        }
      }
    });

    room.on(RoomEvent.TrackMuted, () => this.syncParticipants());
    room.on(RoomEvent.TrackUnmuted, () => this.syncParticipants());

    room.on(RoomEvent.ConnectionQualityChanged, (quality: ConnectionQuality, participant: Participant) => {
      const current = store().voiceParticipants[participant.identity];
      if (!current) return;
      store().setVoiceParticipant({ ...current, connectionQuality: QUALITY_MAP[quality] ?? 'unknown' });
    });

    room.on(RoomEvent.Reconnecting, () => store().patchVoice({ status: 'reconnecting' }));
    room.on(RoomEvent.Reconnected, () => store().patchVoice({ status: 'connected' }));

    room.on(RoomEvent.Disconnected, () => {
      if (useStore.getState().voice.status !== 'idle') void this.leave(true);
    });
  }

  private syncParticipants() {
    const room = this.room;
    if (!room) return;
    const store = useStore.getState();
    const all: Participant[] = [room.localParticipant, ...room.remoteParticipants.values()];
    for (const participant of all) {
      store.setVoiceParticipant({
        identity: participant.identity,
        speaking: participant.isSpeaking,
        micMuted: !participant.isMicrophoneEnabled,
        connectionQuality: QUALITY_MAP[participant.connectionQuality] ?? 'unknown',
      });
    }
  }

  async leave(notifyServer = true) {
    const store = useStore.getState();
    const { guildId } = store.voice;

    for (const element of this.audioElements.values()) element.remove();
    this.audioElements.clear();

    if (this.room) {
      await this.room.disconnect();
      this.room = null;
    }

    store.clearVoice();
    if (notifyServer) {
      if (guildId) gateway.updateVoiceState(guildId, null);
      await api.leaveVoice().catch(() => undefined);
    }
  }

  async toggleMic() {
    const store = useStore.getState();
    if (!this.room || !store.voice.canPublish.includes('microphone')) return;
    const enabled = !store.voice.micEnabled;
    await this.room.localParticipant.setMicrophoneEnabled(enabled);
    store.patchVoice({ micEnabled: enabled });
    if (store.voice.guildId && store.voice.channelId) {
      gateway.updateVoiceState(store.voice.guildId, store.voice.channelId, { selfMute: !enabled });
    }
  }

  /** Ensurdecer corta a saida de audio e tambem o proprio microfone. */
  /**
   * Volume de uma pessoa so para voce, de 0 a 1. Nao muda nada para os outros.
   * O maximo e 1 porque e o teto de HTMLMediaElement.volume; passar disso
   * exigiria reencaminhar o audio por um GainNode do Web Audio, e mexer no
   * caminho do audio de uma call em producao nao vale o ganho.
   */
  setUserVolume(identity: string, volume: number) {
    const store = useStore.getState();
    store.setVolume(identity, volume);
    if (store.voice.deafened) return;
    for (const element of this.audioElements.values()) {
      if (element.dataset.identity === identity) element.volume = Math.max(0, Math.min(1, volume));
    }
  }

  async toggleDeafen() {
    const store = useStore.getState();
    const deafened = !store.voice.deafened;
    // Ao voltar a ouvir, cada um volta ao volume que voce tinha escolhido.
    for (const element of this.audioElements.values()) {
      element.volume = deafened ? 0 : volumeDe(element.dataset.identity ?? '');
    }

    if (this.room && store.voice.canPublish.includes('microphone')) {
      await this.room.localParticipant.setMicrophoneEnabled(deafened ? false : store.voice.micEnabled);
    }
    store.patchVoice({ deafened, micEnabled: deafened ? false : store.voice.micEnabled });
    if (store.voice.guildId && store.voice.channelId) {
      gateway.updateVoiceState(store.voice.guildId, store.voice.channelId, {
        selfDeaf: deafened,
        selfMute: deafened ? true : !store.voice.micEnabled,
      });
    }
  }

  async toggleCamera() {
    const store = useStore.getState();
    if (!this.room || !store.voice.canPublish.includes('camera')) return;
    const enabled = !store.voice.cameraEnabled;
    await this.room.localParticipant.setCameraEnabled(enabled);
    store.patchVoice({ cameraEnabled: enabled });

    /*
     * setCameraEnabled(false) silencia a faixa, nem sempre despublica. Quando
     * so silencia, LocalTrackUnpublished nao dispara, a faixa continua no
     * estado e o <video> fica exibindo um quadro morto — a tela preta no lugar
     * do avatar. Limpar aqui vale nos dois casos.
     */
    if (!enabled) {
      store.setTrack(this.room.localParticipant.identity, 'camera', undefined);
    }

    if (store.voice.guildId && store.voice.channelId) {
      gateway.updateVoiceState(store.voice.guildId, store.voice.channelId, { camera: enabled });
    }
  }

  /**
   * Quem escolhe a tela e o proprio navegador. Pedindo audio junto, o seletor
   * do Chrome/Edge oferece a caixa "compartilhar audio" da aba ou do sistema.
   */
  async startScreenShare(withAudio = true) {
    const store = useStore.getState();
    if (!this.room || !store.voice.canPublish.includes('screen_share')) {
      store.toast('Voce nao tem permissao para compartilhar tela.', 'error');
      return;
    }

    const blocked = screenShareUnavailableReason();
    if (blocked) {
      store.toast(blocked, 'error');
      return;
    }

    try {
      /*
       * A resolucao e escolhida por quem transmite e fica guardada. 1080p num
       * upload apertado, ou com a VPS de um nucleo encaminhando para varios,
       * engasga; 720p costuma ser o ponto em que texto ainda se le e a imagem
       * para de travar.
       */
      const qualidade = qualidadeDaTela();
      const publicacoes = await this.room.localParticipant.setScreenShareEnabled(
        true,
        {
          audio: withAudio,
          systemAudio: withAudio ? 'include' : 'exclude',
          resolution: qualidade.resolution,
        },
        { screenShareEncoding: qualidade.encoding },
      );

      /*
       * contentHint 'detail' diz ao codificador para preservar nitidez em vez
       * de suavizar o movimento. Numa tela de codigo ou planilha, texto legivel
       * vale mais que 30 quadros por segundo; sem isso a letra pequena borra
       * assim que a banda aperta.
       */
      const faixa = publicacoes?.track?.mediaStreamTrack;
      if (faixa) faixa.contentHint = 'detail';

      store.patchVoice({ sharingScreen: true });
      if (store.voice.guildId && store.voice.channelId) {
        gateway.updateVoiceState(store.voice.guildId, store.voice.channelId, { streaming: true });
      }
    } catch (error) {
      // Fechar o seletor do navegador nao e erro: nao vale poluir a tela.
      const name = (error as { name?: string }).name;
      if (name === 'NotAllowedError' || name === 'AbortError') return;
      const message = error instanceof Error ? error.message : 'Nao foi possivel compartilhar a tela.';
      store.toast(message, 'error');
    }
  }

  async stopScreenShare() {
    const store = useStore.getState();
    if (!this.room) return;
    await this.room.localParticipant.setScreenShareEnabled(false);
    store.patchVoice({ sharingScreen: false });
    if (store.voice.guildId && store.voice.channelId) {
      gateway.updateVoiceState(store.voice.guildId, store.voice.channelId, { streaming: false });
    }
  }
}

export const voice = new VoiceManager();
